import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { translate, translateObject } from '@jsverse/transloco';
import moment from 'moment';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';

import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AiInsight } from 'app/providers/semantria-api/ai-insights.service';
import { Constants } from '../../../../constants/constants';
import { ChartDisplayOptions, WidgetDataSource } from '../../../../models';
import { AuthService } from '../../../../providers/bridge-api/auth.service';
import { MatchmakerHandler } from '../../../../providers/semantria-api/spote.service';
import {
  AccountMetadata,
  StorageAccountService,
} from '../../../../providers/storage-api/blender-account.service';
import { AlertService } from '../../../../services/alert.service';
import { SystemService } from '../../../../services/system.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { Utils } from '../../../../utils/utils';
import { FilterService, WidgetQueryOptions } from '../../../collection/services/filter.service';
import { DashboardStore } from '../../../dashboard/stores/dashboard.store';
import { Field, FieldService } from './../../../../providers//storage-api/field.service';
import {
  Comment,
  CommentPoint,
  CommentTarget,
  Comparison,
  Dashboard,
  Widget,
  WidgetDefault,
  WidgetGroup,
} from './../../../../providers/bridge-api/dashboard.service';
import { RealtimeConnection, RealtimeMessage } from './../../../../providers/realtime.service';
import {
  InteractiveSmartSummary,
  InteractiveSmartSummaryService,
} from './../../../../providers/storage-api/interactive-smart-summary.service';
import { DocumentViewOptions } from './../../../collection/components/document/document-view/document-view.component';
import { DatasourceManagerStore } from './../../../dashboard/stores/datasource-manager.store';
import { Project } from '../../../../providers/storage-api/project.service';
import { InteractiveSmartSummaryOptions } from '../interactive-smart-summary/interactive-smart-summary.component';

import { WidgetAnalyticItemDialogResult } from '../widget-analytic-item-dialog/widget-analytic-item-dialog.component';
import {
  MatchmakerHandlerValidationResult,
  WidgetAiInsightsValidatorService,
} from '../widget-expansion-panel/widget-ai-insights/services/validation/widget-ai-insights-validator.service';
import { CommentDataChange } from '../../services/chart-report/chart-report-abstract/chart-report-abstract';
import { PptxReport } from '../../services/pptx.service';
import {
  ExpansionPanelType,
  WidgetCommentsHandlerService,
} from '../../services/widget-comments-handler.service';
import { WidgetComponent } from './widget.component';

export const WIDGET_MIN_WIDTH = 2;
export const WIDGET_MIN_HEIGHT = 4;

@Directive()
export abstract class AbstractWidgetComponent<T extends WidgetDefault>
  implements OnInit, OnDestroy
{
  @Input() public dashboard: Dashboard;
  @Input() protected widget: Widget<T>;
  @Input() public widgetDataSource: WidgetDataSource;
  @Input() public widgetGroup: WidgetGroup;
  @Input() public comparison: Comparison;
  @Input() interactiveSmartSummaryEnabled = false;
  @Input() project: Project;
  protected readonly dashboardStore = inject(DashboardStore);

  @Output() protected needRedraw = new EventEmitter<boolean>();
  @Output() loadingStarted = new EventEmitter<boolean>();
  @Output() loadingFinished = new EventEmitter<boolean>();
  @Output() updateTitle = new EventEmitter<void>();
  @Output() aiInsightPointSelection = new EventEmitter<{ point: any; isSelected: boolean }[]>();

  @ViewChild('elementContent', /* TODO: add static flag */ {})
  elementContent: ElementRef<HTMLDivElement>;

  protected readonly ANALYTIC_ITEM_MAPPING = {
    sentiment_topics: 'analyses.topics.sentimentPolarity',
    sentiment_entities: 'analyses.entities.sentimentPolarity',
    sentiment_phrases: 'analyses.phrases.sentimentPolarity',
    sentiment_taxonomy: 'analyses.taxonomy.sentimentPolarity',
    sentiment_themes: 'analyses.themes.sentimentPolarity',
    clauseModel: 'ML-analyses.eie.title',
    taxonomy: 'analyses.taxonomy.taxonomyFullPath',
    topics: 'ML-analyses.topics.title',
    entities: 'ML-analyses.entities.title',
    phrases: 'analyses.phrases.title',
    themes: 'analyses.themes.normalizedTitle',
  };

  isLoading = false;
  as_data_table = undefined;
  dataSourceNotFound = false;
  needDataSource = true;
  readonly downloadFormats: string[] = ['pptx', 'image'];
  canFilter = false;
  filterDisabled = false;
  weightDisabled = true;
  weightTooltip = translate('widgets.no-weight-available');
  weightFields = [];
  canBeDeleted = true;
  canBeCloned = true;
  canRemoveFromIPWG = true;
  removeFromIPWGTooltip: string;
  hasAnalyses = false;
  minWidth = WIDGET_MIN_WIDTH;
  minHeight = WIDGET_MIN_HEIGHT;
  _filteredDocumentCount: number;
  isDirty = false;
  widgetInfo: {
    icon: string;
    level: 'INFO' | 'WARNING' | 'ERROR';
    tooltip: string;
  };
  widgetDataSourceId = undefined;
  widgetLoadController = new AbortController();
  protected groupFieldSub$: Subscription;
  protected fieldDetailsMap: Map<string, Field> = new Map<string, Field>();
  protected accountMetadata: AccountMetadata;
  showDocumentViewModal: boolean;
  showSmartSummaryModal: boolean;
  showSmartSummaryOptionsModal: boolean;
  documentViewModalOptions: DocumentViewOptions;
  interactiveSmartSummaryOptions: InteractiveSmartSummaryOptions;
  interactiveSmartSummaryMenu: {
    tooltip?: string;
    enabled: boolean;
    label: string;
  } = {
    tooltip: '',
    label: '',
    enabled: false,
  };
  removeInteractiveSmartSummaryMenu: {
    tooltip?: string;
    enabled: boolean;
    label: string;
  } = {
    tooltip: '',
    label: '',
    enabled: false,
  };
  pointName: string;
  pointDisplayName: string;
  pointComments: Comment[] = [];
  pointSummaries: InteractiveSmartSummary[];
  pointHasDocuments: boolean;
  storedSmartSummaries: InteractiveSmartSummary[];
  realtimeHandler: RealtimeConnection;
  isSmartSummaryGenerating = false;

  destroy$ = new Subject<void>();

  canCreateSmartSummary: boolean;

  readonly datasourceManagerStore = inject(DatasourceManagerStore);
  aiInsightValidatorService = inject(WidgetAiInsightsValidatorService);
  protected cdr = inject(ChangeDetectorRef);

  protected constructor(
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected filterService: FilterService,
    protected alertService: AlertService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected widgetComponent: WidgetComponent,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {}

  ngOnInit() {
    if (this.widget.cols < this.minWidth) {
      this.widget.cols = this.minWidth;
      this.needRedraw.emit(true);
    }

    if (this.widget.rows < this.minHeight) {
      this.widget.rows = this.minHeight;
      this.needRedraw.emit(true);
    }
    if (
      this.needDataSource &&
      this.datasourceManagerStore &&
      this.widget.dataSourceId &&
      !this.datasourceManagerStore.getById(this.widget.dataSourceId)
    ) {
      this.dataSourceNotFound = true;
    }

    this.accountService.groupFieldsSub.takeUntil(this.destroy$).subscribe((accountMetadata) => {
      this.accountMetadata = accountMetadata;

      this.fieldDetailsMap.clear();
      accountMetadata.metadata.forEach((f) => this.fieldDetailsMap.set(f.fieldName, f));

      this.updateWeightingProperties(this.widgetDataSource);
      this.cdr.markForCheck();
    });

    this.realtimeHandler = this.interactiveSmartSummaryService.on(
      this.dashboard.project_id,
      this.handleSmartSummaryEvent.bind(this)
    );

    this.commentsHandlerService.showExpansionPanel$
      .pipe(
        filter((event) => this.widget?.id === event.widgetId),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => this.showExpansionPanel(event.panelType, event.panelData));

    this.commentsHandlerService.hideExpansionPanel$
      .pipe(
        filter((event) => this.widget?.id === event.widgetId),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => this.hideExpansionPanel(event.panelType));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.realtimeHandler) {
      this.realtimeHandler.disconnect();
    }
  }

  private handleSmartSummaryEvent(data: RealtimeMessage<InteractiveSmartSummary>) {
    if (data.object.widgetId !== this.widget.id) {
      return;
    }

    if (data.verb === 'CREATE') {
      this.storedSmartSummaries = this.storedSmartSummaries || [];
      this.storedSmartSummaries = this.storedSmartSummaries.filter(
        (s) =>
          !s.id.startsWith('TK') ||
          s.payload.analyticItemName !== data.object.payload.analyticItemName ||
          s.payload.analyticItemType !== data.object.payload.analyticItemType ||
          s.payload.query !== data.object.payload.query
      );
      this.storedSmartSummaries.push(data.object);

      this.pointSummaries = this.getSmartSummariesForPoint(
        data.object.payload.analyticItemName,
        data.object.payload.query
      );
    } else if (data.verb === 'UPDATE') {
      const storedIdx = this.storedSmartSummaries.findIndex((d) => d.id === data.object.id);
      if (storedIdx > -1) {
        this.storedSmartSummaries.splice(storedIdx, 1, data.object);
      }

      const forPointIdx = this.pointSummaries?.findIndex((d) => d.id === data.object.id);
      if (forPointIdx > -1) {
        this.pointSummaries.splice(forPointIdx, 1, data.object);
      }

      if (data.object.status === 'COMPLETED' && this.isSmartSummaryGenerating) {
        this.isSmartSummaryGenerating = false;
        this.alertService.showToast(
          translate('widgets.interactive-smart-summary.summary-generating-finished', {
            pointName: data.object.payload.analyticItemName,
            widgetTitle: this.widgetTitle,
          })
        );
      }

      if (data.object.status === 'ERROR') {
        this.isSmartSummaryGenerating = false;
        this.alertService.showErrorToast(
          translate('widgets.interactive-smart-summary.summary-generating-error', {
            pointName: data.object.payload.analyticItemName,
            widgetTitle: this.widgetTitle,
          })
        );
      }
    } else if (data.verb === 'DELETE') {
      this.storedSmartSummaries = this.storedSmartSummaries.filter((d) => d.id !== data.object.id);
      this.pointSummaries = this.pointSummaries.filter((d) => d.id !== data.object.id);
    }
    if (this.isSmartSummaryEnabled) {
      this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
      this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
    }
    this.cdr.markForCheck();
  }

  protected buildSmartSummaryMenu(isTotalVolume = false) {
    if (this.payload.transpose) {
      return {
        tooltip: translate('widgets.smart-summary.transpose-tooltip'),
        label: translate('widgets.smart-summary.label-generate'),
        enabled: false,
      };
    }
    if (isTotalVolume) {
      return {
        tooltip: translate('widgets.smart-summary.total-volume-tooltip'),
        label: translate('widgets.smart-summary.label-generate'),
        enabled: false,
      };
    }

    if (!this.storedSmartSummaries) {
      return {
        tooltip: translate('widgets.smart-summary.label-loading-tooltip'),
        label: translate('widgets.smart-summary.label-loading'),
        enabled: false,
      };
    }

    if (this.isSmartSummaryGenerating) {
      return {
        tooltip: translate('widgets.smart-summary.label-generating-tooltip'),
        label: translate('widgets.smart-summary.label-generating'),
        enabled: false,
      };
    }

    if (!this.pointHasDocuments) {
      return {
        tooltip: translate('widgets.smart-summary.no-documents-tooltip'),
        label: translate('widgets.smart-summary.label-generate'),
        enabled: false,
      };
    }

    if (!this.pointSummaries?.length) {
      if (!this.canCreateSmartSummary) {
        return {
          tooltip: translate(
            'widgets.smart-summary.unavailable-for-points-with-less-than-documents',
            { count: Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT }
          ),
          label: translate('widgets.smart-summary.label-generate'),
          enabled: false,
        };
      }
      return {
        tooltip: '',
        label: translate('widgets.smart-summary.label-generate'),
        enabled: true,
      };
    }

    if (this.pointSummaries[0].status === 'ERROR') {
      return {
        tooltip: translate('widgets.smart-summary.label-error-tooltip'),
        label: translate('widgets.smart-summary.label-error'),
        enabled: false,
      };
    }

    return {
      tooltip: '',
      label: translate('widgets.smart-summary.label-open'),
      enabled: true,
    };
  }

  protected buildSmartSummaryRemoveMenu() {
    const tooltip = translate('widgets.smart-summary.remove-summary');
    const label = translate('widgets.smart-summary.label-remove');
    let enabled = false;

    if (this.pointSummaries?.length > 0) {
      enabled = true;

      if (this.pointSummaries[0].status === 'RUNNING') {
        enabled = false;
      }
    }

    if (!this.isSmartSummaryEnabled) {
      enabled = false;
    }

    return {
      tooltip,
      label,
      enabled,
    };
  }

  updateWeightingProperties(dataSource: WidgetDataSource): void {
    this.weightDisabled = true;
    this.weightTooltip = translate('widgets.no-weight-available');

    if (!dataSource?.customFields) {
      return;
    }

    const metadataFields = this.customFieldService.getMetadataSubTypes(
      dataSource,
      this.accountMetadata
    );

    this.weightFields = [...this.fieldDetailsMap.values()]
      .filter((f) => f.metadata?.weight && metadataFields.includes(f.fieldName))
      .map((f) => ({
        ...f,
        displayName: f.description,
      }))
      .sort((f1, f2) =>
        f1.displayName.localeCompare(f2.displayName, undefined, { sensitivity: 'accent' })
      );

    if (this.weightFields.length) {
      this.weightDisabled = false;
      this.weightTooltip = translate('widgets.no-weighting');
      // TODO check if we need to replace customFields check with analyticItemTypes
      if (this.payload.weightField && dataSource.customFields.includes(this.payload.weightField)) {
        this.weightTooltip = FieldService.getFieldLongName(this.widget.payload.weightField);
      }
    }
  }

  public get isLocked(): boolean {
    return this.dashboard.properties?.is_locked || this.widget.properties.is_locked;
  }

  public get canDownload(): boolean {
    return this.downloadFormats.length > 0;
  }

  public get defaultTitle(): string {
    return translate('widgets.untitled-widget');
  }

  public get hasFilters(): boolean {
    const hasDocumentFilters: boolean = FilterService.analyticItemFilterSetsHasFilters(
      this.widget.payload.filters,
      this.widget.payload.userSearch
    );
    const hasSectionFilterSelection: boolean =
      Array.isArray(this.widget.payload.sectionFilter) &&
      this.widget.payload.sectionFilter.length > 0;
    return hasDocumentFilters || hasSectionFilterSelection;
  }

  public get payload(): T {
    return this.widget.payload;
  }

  public onResize(_data: any): void {
    this.redraw();
  }

  public analyticTypeSet(_result: WidgetAnalyticItemDialogResult): void {
    // default implementation does nothing
  }

  public handleChartOptionsChanging(): void {
    // default implementation does nothing
  }

  public shouldRefreshAfterChartOptionsChange(_options: ChartDisplayOptions): boolean {
    return _options.needRefresh;
  }

  public applyComparison(_comparison: Comparison): void {
    // default implementation does nothing
  }

  revokeComparison(): void {
    // default implementation does nothing
  }

  public async refresh(
    _reset = true,
    _initialLoading = false,
    _refreshedDatasource?: WidgetDataSource,
    _isDataSourceChanged = false
  ): Promise<void> {
    // default implementation does nothing
  }

  public redraw(): void {
    // default implementation does nothing
  }

  public async exportAsImage(
    _format: 'blob' | 'canvas' = 'blob'
  ): Promise<Blob | HTMLCanvasElement> {
    return; // default implementation does nothing
  }

  public async exportAsPptx(_pptx: PptxReport): Promise<void> {
    return; // default implementation does nothing
  }

  public async exportSelectedInsightsAsPptx(
    _pptx: PptxReport,
    _insights: AiInsight[]
  ): Promise<void> {
    return; // default implementation does nothing
  }

  public async exportAsTable(): Promise<void> {
    return; // default implementation does nothing
  }

  public async goBackToRoot(): Promise<void> {
    return; // default implementation does nothing
  }

  public async exportAsCsv(): Promise<{ fileName?: string; data: any } | void> {
    return;
  }

  public canShowDataTable(): boolean {
    return true;
  }

  public isDocumentFilterGroupSelectEnabled(): boolean {
    return true;
  }

  public canShowDocumentList(): boolean {
    return false;
  }

  public canCreateCollection(): boolean {
    return true;
  }

  public widgetMenuDisplayOnHover(): boolean {
    return false;
  }

  canHideTitle(): boolean {
    return false;
  }

  public setWidget(widget: Widget<T>) {
    this.widget = widget;
  }

  public get hasWidgetGroup() {
    return !!this.widgetGroup;
  }

  public get isWidgetGroupParent() {
    if (
      this.widgetGroup &&
      this.widgetGroup.parentWidget &&
      this.widgetGroup.parentWidget === this.widget.id
    ) {
      return true;
    }
    return false;
  }

  public setWidgetGroup(group: WidgetGroup) {
    this.widgetGroup = group;
  }

  public getWidgetGroup() {
    return this.widgetGroup;
  }

  public gethasCustomSet() {
    if (this.widget.payload.hasOwnProperty('selectedItems')) {
      return this.widget.payload['selectedItems'].length > 0;
    }
  }

  protected mapAnalyticItemToFilterAnalyticItem(analyticItem) {
    if (this.widgetQueryOptions.customFieldSelected) {
      return 'metadata.' + analyticItem;
    }
    if (this.ANALYTIC_ITEM_MAPPING.hasOwnProperty(analyticItem)) {
      return this.ANALYTIC_ITEM_MAPPING[analyticItem];
    }
    return analyticItem;
  }

  public updateFilterList() {
    if (this.widget.payload.hasOwnProperty('filters')) {
      const filters = this.widget.payload['filters'];
      if (
        filters &&
        (filters.hasOwnProperty('and') ||
          filters.hasOwnProperty('or') ||
          filters.hasOwnProperty('not'))
      ) {
        const tempFilters = FilterService.getDefaultFilterSet();
        const tempHolder = JSON.parse(JSON.stringify(filters));

        if (filters.hasOwnProperty('and')) {
          tempHolder['and'].forEach((filterItem) => {
            if (filterItem.analyticItem.includes('topics')) {
              tempFilters.topics.and.push(filterItem);
            }
            if (filterItem.analyticItem.includes('entities')) {
              tempFilters.entities.and.push(filterItem);
            }
            if (filterItem.analyticItem.includes('themes')) {
              tempFilters.themes.and.push(filterItem);
            }
            if (filterItem.analyticItem.includes('phrases')) {
              tempFilters.phrases.and.push(filterItem);
            }
            if (filterItem.analyticItem.includes('analyses.sentimentPolarity')) {
              tempFilters.analyses.and.push(filterItem);
            }
            if (filterItem.analyticItem.includes('metadata')) {
              tempFilters.metadata.and.push(filterItem);
            }
            if (filterItem.analyticItem.includes('createdAt')) {
              tempFilters.dates.and.push(filterItem);
            }
            if (filterItem.analyticItem.includes('tags')) {
              tempFilters.tags.and.push(filterItem);
            }
          });
        }

        if (filters.hasOwnProperty('or')) {
          tempHolder['or'].forEach((filterItem) => {
            if (filterItem.analyticItem.includes('topics')) {
              tempFilters.topics.or.push(filterItem);
            }
            if (filterItem.analyticItem.includes('entities')) {
              tempFilters.entities.or.push(filterItem);
            }
            if (filterItem.analyticItem.includes('themes')) {
              tempFilters.themes.or.push(filterItem);
            }
            if (filterItem.analyticItem.includes('phrases')) {
              tempFilters.phrases.or.push(filterItem);
            }
            if (filterItem.analyticItem.includes('analyses.sentimentPolarity')) {
              tempFilters.analyses.or.push(filterItem);
            }
            if (filterItem.analyticItem.includes('metadata')) {
              tempFilters.metadata.or.push(filterItem);
            }
            if (filterItem.analyticItem.includes('createdAt')) {
              tempFilters.dates.or.push(filterItem);
            }
            if (filterItem.analyticItem.includes('tags')) {
              tempFilters.tags.or.push(filterItem);
            }
          });
        }

        if (filters.hasOwnProperty('not')) {
          tempHolder['not'].forEach((filterItem) => {
            if (filterItem.analyticItem.includes('topics')) {
              tempFilters.topics.not.push(filterItem);
            }
            if (filterItem.analyticItem.includes('entities')) {
              tempFilters.entities.not.push(filterItem);
            }
            if (filterItem.analyticItem.includes('themes')) {
              tempFilters.themes.not.push(filterItem);
            }
            if (filterItem.analyticItem.includes('phrases')) {
              tempFilters.phrases.not.push(filterItem);
            }
            if (filterItem.analyticItem.includes('analyses.sentimentPolarity')) {
              tempFilters.analyses.not.push(filterItem);
            }
            if (filterItem.analyticItem.includes('metadata')) {
              tempFilters.metadata.not.push(filterItem);
            }
            if (filterItem.analyticItem.includes('createdAt')) {
              tempFilters.dates.not.push(filterItem);
            }
            if (filterItem.analyticItem.includes('tags')) {
              tempFilters.tags.not.push(filterItem);
            }
          });
        }
        (this.widget.payload as any).filters = JSON.parse(JSON.stringify(tempFilters));
      }
      if (
        !this.widget.payload['filters'].hasOwnProperty('tags') ||
        !this.widget.payload['filters'].hasOwnProperty('core')
      ) {
        if (!this.widget.payload['filters'].hasOwnProperty('tags')) {
          this.widget.payload['filters']['tags'] = { and: [], or: [], not: [] };
        }
        if (!this.widget.payload['filters'].hasOwnProperty('core')) {
          this.widget.payload['filters']['core'] = { and: [], or: [], not: [] };
        }

        this.widget.payload['filters']['metadata'].and.forEach((filterItem, index) => {
          if (filterItem.analyticItem.includes('tags')) {
            this.widget.payload['filters']['tags'].and.push(filterItem);
            this.widget.payload['filters']['metadata'].and.splice(index, 1);
          }

          if (filterItem.analyticItem.includes('creator')) {
            this.widget.payload['filters']['core'].and.push(filterItem);
            this.widget.payload['filters']['metadata'].and.splice(index, 1);
          }

          if (filterItem.analyticItem.includes('source')) {
            this.widget.payload['filters']['core'].and.push(filterItem);
            this.widget.payload['filters']['metadata'].and.splice(index, 1);
          }
        });

        this.widget.payload['filters']['metadata'].or.forEach((filterItem, index) => {
          if (filterItem.analyticItem.includes('tags')) {
            this.widget.payload['filters']['tags'].or.push(filterItem);
            this.widget.payload['filters']['metadata'].or.splice(index, 1);
          }

          if (filterItem.analyticItem.includes('creator')) {
            this.widget.payload['filters']['core'].or.push(filterItem);
            this.widget.payload['filters']['metadata'].or.splice(index, 1);
          }

          if (filterItem.analyticItem.includes('source')) {
            this.widget.payload['filters']['core'].or.push(filterItem);
            this.widget.payload['filters']['metadata'].or.splice(index, 1);
          }
        });

        this.widget.payload['filters']['metadata'].not.forEach((filterItem, index) => {
          if (filterItem.analyticItem.includes('tags')) {
            this.widget.payload['filters']['tags'].not.push(filterItem);
            this.widget.payload['filters']['metadata'].not.splice(index, 1);
          }

          if (filterItem.analyticItem.includes('creator')) {
            this.widget.payload['filters']['core'].not.push(filterItem);
            this.widget.payload['filters']['metadata'].not.splice(index, 1);
          }

          if (filterItem.analyticItem.includes('source')) {
            this.widget.payload['filters']['core'].not.push(filterItem);
            this.widget.payload['filters']['metadata'].not.splice(index, 1);
          }
        });
      }
      if (!this.widget.payload['filters'].hasOwnProperty('taxonomy')) {
        this.widget.payload['filters']['taxonomy'] = { and: [], or: [], not: [] };
      }
      if (!this.widget.payload['filters'].hasOwnProperty('sectionProperties')) {
        this.widget.payload['filters']['sectionProperties'] = { and: [], or: [], not: [] };
      }
      //Remove all not polarity feature filters
      this.widget.payload['filters']['themes'].not = this.widget.payload['filters'][
        'themes'
      ].not.filter((f) => !f.analyticItem.includes('sentimentPolarity'));
      this.widget.payload['filters']['phrases'].not = this.widget.payload['filters'][
        'phrases'
      ].not.filter((f) => !f.analyticItem.includes('sentimentPolarity'));
      this.widget.payload['filters']['topics'].not = this.widget.payload['filters'][
        'topics'
      ].not.filter((f) => !f.analyticItem.includes('sentimentPolarity'));
      this.widget.payload['filters']['entities'].not = this.widget.payload['filters'][
        'entities'
      ].not.filter((f) => !f.analyticItem.includes('sentimentPolarity'));
    } else {
      if (
        this.widget.component !== 'DashboardWidgetMarkdownComponent' &&
        this.widget.component !== 'DashboardWidgetNotesComponent' &&
        this.widget.component !== 'DashboardWidgetPredictiveComponent'
      ) {
        (this.widget.payload as any).filters = FilterService.getDefaultFilterSet();
      }
    }
  }

  protected getDocumentsQuery(_initialQuery?: string) {
    return this.filterService.getQueryForDocuments(this.widgetQueryOptions);
  }

  protected addSlideOptions(dataOptions: any) {
    if (this.widgetDataSource) {
      dataOptions['dataSource'] = this.widgetDataSource.displayName;
      dataOptions['docCount'] = this.filteredDocumentCount ?? this.widgetDataSource.documentCount;
      dataOptions['maxDocs'] = this.widgetDataSource.documentCount;
    }
  }

  protected async exportImageToPptx(blob, pptx, widgetTitle, imageOptions?, slide?) {
    const imageData = await this.systemService.readBlob(blob);
    const dataOptions = [];
    this.addSlideOptions(dataOptions);
    return pptx.addImageSlide(
      { data: imageData, ...imageOptions },
      {
        title: widgetTitle,
        data: dataOptions,
      },
      slide
    );
  }

  protected addTitleSlideToPptx(pptx) {
    const translations = translateObject('widgets.pptx');
    const rows = [
      [{ text: translations['widget-name'] }, { text: this.widgetTitle }],
      [{ text: translations['dashboard-name'] }, { text: this.dashboard.title }],
      [{ text: translations['datasource-name'] }, { text: this.widgetDataSource.displayName }],
      [{ text: translations['analysis-id'] }, { text: this.widgetDataSource.analysisId }],
      [{ text: translations['group-id'] }, { text: this.widgetDataSource.groupId }],
      [{ text: translations['generated'] }, { text: moment().format('LLL') }],
    ];

    const dataOptions = {};
    const tableWidth = 12; // in inches
    this.addSlideOptions(dataOptions);

    pptx.addTableSlide(
      rows,
      {
        data: dataOptions,
        autoPageLineWeight: -0.3,
        w: tableWidth,
        border: {
          color: 'black',
          pt: 2,
          type: 'solid',
        },
      },
      true
    );

    return dataOptions;
  }

  protected createNewCanvasWithTitle(canvas: HTMLCanvasElement): HTMLCanvasElement {
    // create a new bigger one to have title
    const newCanvas = document.createElement('canvas');
    newCanvas.setAttribute('width', canvas.width.toString());
    newCanvas.setAttribute('height', (canvas.height + 40).toString());

    const ctx = newCanvas.getContext('2d');

    // add white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);
    ctx.resetTransform();

    // copy the widget
    ctx.drawImage(canvas, 0, 40, canvas.width, canvas.height);
    ctx.resetTransform();

    // add title
    ctx.font = 'bold 20px "IBM Plex Sans", Arial';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#253746';
    ctx.fillText(this.widgetTitle, canvas.width / 2, 24);

    return newCanvas;
  }

  drawCanvasResult(canvasElements: HTMLCanvasElement[]): {
    dataUrl: string;
    body: { filename: string; svg: HTMLCanvasElement };
  } {
    const MARGIN = 20;

    const result = document.createElement('canvas');

    const width =
      canvasElements.map((c) => c.width).reduce((a, b) => a + b, 0) +
      canvasElements.length * MARGIN;
    const height = Math.max(...canvasElements.map((c) => c.height));

    result.setAttribute('width', width.toString());
    result.setAttribute('height', height.toString());

    const ctx = result.getContext('2d');

    ctx.fillStyle = '#F2F7FF';
    ctx.fillRect(0, 0, result.width, result.height);
    ctx.resetTransform();

    let offset = 0;
    for (const canvasElement of canvasElements) {
      ctx.drawImage(canvasElement, offset, 0, canvasElement.width, canvasElement.height);
      ctx.resetTransform();

      offset += canvasElement.width + MARGIN;
    }

    return {
      dataUrl: result.toDataURL('image/svg'),
      body: {
        filename: this.defaultTitle,
        svg: result,
      },
    };
  }

  protected get widgetTitle() {
    return this.widget.title ? this.widget.title : this.defaultTitle;
  }

  public enableHideItems() {
    return (
      this.widget.payload.analyticItem !== 'documents' &&
      (this.widget.payload.selectedItems?.length > 1 || !this.widget.payload.selectedItems?.length)
    );
  }

  get isReadOnly() {
    return this.widgetComponent.isReadOnly;
  }

  public actionsDisabled() {
    return false;
  }

  public isDataSourceChangeDisabled(): boolean {
    return false;
  }

  public isAnalyticItemChangeDisabled(): boolean {
    return false;
  }

  public canChangeAnalyticItem(): boolean {
    return true;
  }

  public canAddToWidgetGroup(): boolean {
    return true;
  }

  public canAddToComparison(): boolean {
    return true;
  }

  public isWidgetGroupChangeDisabled(): boolean {
    return !!this.dashboard.locked;
  }

  public canApplyComparison(comparison?: Comparison): boolean {
    if (!comparison) {
      return true;
    }

    return comparison.filters.length < 2;
  }

  public getComparisonErrorMessage(_comparison?: Comparison): string | undefined {
    return undefined;
  }

  public isComparisonChangeDisabled(): boolean {
    return !!this.dashboard.locked;
  }

  public get hasInvalidComparison() {
    return this.comparison && !this.canApplyComparison(this.comparison);
  }

  commentDialogTooltip() {
    if (!this.canOpenCommentDialog) {
      return translate('widgets.comments.not-enabled');
    }
  }

  addCommentDialogToolTip() {
    if (this.isReadOnly) {
      return translate('widgets.comments.not-enabled');
    }
  }

  openCommentDialogToolTip(point) {
    if (!this.hasComment(point)) {
      return translate('widgets.comments.no-comments');
    }
  }

  hasComment(item?) {
    if (!item) {
      return this.payload.comments?.length;
    }

    return !!this.pointComments?.length;
  }

  get canAddComment() {
    return (
      this.payload.type === 'numericMetadata' ||
      this.payload.type === 'documentCount' ||
      this.payload.type === 'donut' ||
      this.payload.type === 'gauge' ||
      this.payload.type === 'scatterPlot' ||
      this.payload.type === 'sentimentScore' ||
      this.payload.type === 'sentimentPolarity' ||
      this.payload.type === 'sentimentVolume' ||
      this.payload.type === 'taxonomyMap' ||
      this.payload.type === 'breakdown' ||
      (this.payload.type === 'crosstab' && this.widget.payload.graphType === 'crosstab') ||
      this.payload.type === 'grid' ||
      this.payload.type === 'impact' ||
      this.payload.type === 'focusAreas'
    );
  }

  get canOpenCommentDialog() {
    if (this.pointComments?.length) {
      return true;
    }
    return !this.isReadOnly;
  }

  onCommentListOpenClicked(item) {
    this.commentsHandlerService.showExpansionPanel({
      widgetId: this.widget.id,
      widgetContainer: this.widgetComponent.widgetContainer,
      panelData: item,
      panelType: 'COMMENTS',
    });
  }

  openCommentDialog(point) {
    this.widgetComponent.openCommentDialog(point);
  }

  showExpansionPanel(panelType: ExpansionPanelType, expansionPanelData: any) {
    switch (panelType) {
      case 'COMMENTS':
        this.widgetComponent.openCommentList(expansionPanelData);
        return;
      case 'INSIGHTS':
        this.widgetComponent.openAiInsights();
        return;
    }
  }

  hideExpansionPanel(panelType: ExpansionPanelType) {
    switch (panelType) {
      case 'COMMENTS':
        this.widgetComponent.closeCommentList();
        return;
      case 'INSIGHTS':
        this.widgetComponent.closeAiInsights();
        return;
    }
  }

  addComment(comment: Comment) {
    const { ...payload } = this.payload;
    comment.payload = Utils.deepCloneWithNulls(payload);
    comment.payload.widgetGroup = this.widgetGroup?.id;
    comment.payload.widgetId = this.widget.id;
    comment.payload.widgetDataSourceId = this.widget.dataSourceId;
    this.payload.comments = this.payload.comments || [];
    this.payload.comments.push(comment);

    this.handleCommentPinnage(comment);

    this.onCommentAdded(comment);

    this.alertService.showToast(translate('widgets.comments.comment-added'));
  }

  onCommentAdded(_comment: Comment) {}

  deleteComment(comment: Comment) {
    this.payload.comments = this.payload.comments.filter((c) => c.id !== comment.id);

    this.handleCommentPinnage(comment);

    this.onCommentDeleted(comment);

    this.alertService.showToast(translate('widgets.comments.comment-deleted'));
  }

  onCommentDeleted(_comment: Comment) {}

  editComment(comment: Comment) {
    const originalDataSourceId = comment.payload.widgetDataSourceId;

    const { ...payload } = this.payload;
    comment.payload = Utils.deepCloneWithNulls(payload);
    comment.payload.widgetGroup = this.widgetGroup?.id;
    comment.payload.widgetId = this.widget.id;
    comment.payload.widgetDataSourceId = originalDataSourceId;

    this.handleCommentPinnage(comment);

    if (this.isCommentVisible(comment)) {
      this.onCommentEdited(comment);
    }
  }

  onCommentEdited(_comment: Comment) {}

  private handleCommentPinnage(comment: Comment) {
    if (!comment.summaryPinned && !comment.titlePinned) {
      return;
    }

    const pinnedComment = this.payload.comments.find(
      (comm) =>
        comm.target?.pointId === comment.target?.pointId &&
        comm.id !== comment.id &&
        (comm.titlePinned || comm.summaryPinned)
    );

    if (pinnedComment) {
      pinnedComment.titlePinned = false;
      pinnedComment.summaryPinned = false;
    }
  }

  isCommentVisible(comment: Comment) {
    return comment.payload.widgetDataSourceId === this.widget.dataSourceId;
  }

  getVisibleComments(): Comment[] {
    if (this.widget.payload.commentsHidden) {
      return [];
    }
    const safeComments = this.payload.comments ?? [];
    return safeComments.filter((c) => this.isCommentVisible(c));
  }

  getCommentsForPanel(): Comment[] {
    return this.payload.comments ?? [];
  }

  getCommentChanges(): Map<string, CommentDataChange> {
    const changes = new Map();

    this.getVisibleComments().forEach((c) => {
      const change = this.getCommentChangeForComment(c);
      if (change) {
        changes.set(c.id, change);
      }
    });

    return changes;
  }

  getCommentChangeForComment(_comment) {
    return undefined;
  }

  itemToCommentPoint(item): CommentPoint {
    return item;
  }

  itemToCommentAnalyticItem(item) {
    return [
      {
        name: item.pointName,
        displayName: item.pointDisplayName,
        type: this.widget.payload.analyticItem,
        subType: this.widget.payload.entityTypeFilter,
      },
    ];
  }

  public get isSmartSummaryEnabled() {
    return this.interactiveSmartSummaryEnabled;
  }

  public openSmartSummaryById(id) {
    const smartSummary = this.storedSmartSummaries?.find((ss) => ss.id === id);
    this.openSmartSummaryModal(
      smartSummary.payload.analyticItemName,
      smartSummary,
      smartSummary.payload.query
    );
  }

  public openSmartSummary(pointName, query?, filterStr?, analysisId?) {
    if (this.pointName !== pointName) {
      this.pointName = pointName;
      this.pointSummaries = this.getSmartSummariesForPoint(pointName, query);
    }

    if (!this.pointSummaries) {
      return;
    }

    const analysisIdToUse = analysisId ?? this.widgetDataSource.analysisId;

    if (!this.pointSummaries.length) {
      this.isSmartSummaryGenerating = true;

      const queryString = query ?? this.getDocumentsQuery();
      this.interactiveSmartSummaryService
        .createSmartSummaryTask(
          this.dashboard.project_id,
          this.widgetDataSource.collectionName,
          this.widgetDataSource.groupId,
          analysisIdToUse,
          this.widget.id,
          this.dashboard.id,
          {
            analyticItemType: this.widget.payload.analyticItem,
            analyticItemName: pointName,
          },
          this.timezoneService.timezone,
          queryString
        )
        .then((res) => {
          this.alertService.showToast(
            translate('widgets.interactive-smart-summary.summary-generating-started')
          );

          this.storedSmartSummaries = this.storedSmartSummaries || [];
          this.storedSmartSummaries.push({
            projectId: res.taskPayload.projectId,
            dashboardId: res.taskPayload.dashboardId,
            widgetId: res.taskPayload.widgetId,
            dataSourceId: res.taskPayload.analysisGroupId ?? res.taskPayload.analysisId,
            payload: {
              analyticItemType: res.taskPayload.analyticItemType,
              analyticItemName: res.taskPayload.analyticItemName,
              query: res.taskPayload.queryString,
            },
            id: res.id,
            startedAt: res.createdAt,
            status: 'RUNNING',
          });
          if (this.isSmartSummaryEnabled) {
            this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
            this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
          }
        });

      return;
    }

    this.openSmartSummaryModal(
      pointName,
      this.pointSummaries?.[0],
      query,
      filterStr,
      analysisIdToUse
    );
  }

  protected openSmartSummaryModal(
    pointName,
    smartSummary,
    query?,
    filterStr?,
    analysisIdOverride?
  ) {
    const queryString = query ?? this.getDocumentsQuery();
    const filterString =
      filterStr ??
      this.filterService.buildFilterStringClearPolarityFilters(
        this.widget.payload.filters,
        this.widget.payload.analyticItem
      );
    this.interactiveSmartSummaryOptions = {
      widget: this.widget,
      reportFilter: filterString,
      reportQuery: queryString,
      widgetDataSource: this.widgetDataSource,
      analyticItem: this.widget.payload.analyticItem,
      pointName,
      smartSummaryResult: smartSummary,
      canAddComment: this.canAddComment && this.canOpenCommentDialog,
      isReadOnly: this.isReadOnly,
      embeddingModel: undefined,
      embeddingStrategy: undefined,
      isDebug: this.authService.isDebugUser,
      overrideAnalysisId: analysisIdOverride,
      isDashboardLocked: this.dashboard.locked,
    };
    this.showSmartSummaryModal = true;
  }

  removeSmartSummary() {
    if (!this.pointSummaries?.length) {
      return;
    }

    const smartSummaryId = this.pointSummaries[0].id;

    this.interactiveSmartSummaryService.removeSmartSummary(
      this.dashboard.project_id,
      smartSummaryId
    );

    this.pointComments
      ?.filter((c) => c.smartSummaryId === smartSummaryId)
      .forEach((c) => (c.smartSummaryId = undefined));

    this.storedSmartSummaries = this.storedSmartSummaries.filter((d) => d.id !== smartSummaryId);

    this.pointSummaries = this.pointSummaries.filter((d) => d.id !== smartSummaryId);

    this.alertService.showToast(
      translate('widgets.interactive-smart-summary.removed-smart-summary')
    );
  }

  onSmartSummaryClosed(_$event) {
    this.showSmartSummaryModal = false;
  }

  onAddSmartSummaryNotes($event) {
    this.widgetComponent.onAddSmartSummaryNotes($event);
  }

  getSmartSummariesForPoint(pointName, pointQuery?) {
    const query = pointQuery ?? this.getDocumentsQuery() ?? '*:*';
    return this.storedSmartSummaries?.filter(
      (s) =>
        !s.id.startsWith('TK') &&
        s.payload.analyticItemName === pointName &&
        s.payload.analyticItemType === (this.widget.payload.analyticItem ?? 'metadata') &&
        s.payload.query === query
    );
  }

  validateMatchmakerHandlers(handlers?: MatchmakerHandler[]): MatchmakerHandlerValidationResult[] {
    return this.aiInsightValidatorService.validateMatchmakerHandlers(
      handlers,
      this.widget,
      this.widgetDataSource,
      this.getAiInsightDataPointCount.bind(this)
    );
  }

  getAiInsightDataPointCount(_handler: MatchmakerHandler): number {
    return -1;
  }

  getAiInsightEligiblePointCount(): number {
    return 0;
  }

  hasEnoughAiInsightEligiblePoints(_handler: MatchmakerHandler, minRequired: number): boolean {
    const eligiblePoints = this.getAiInsightEligiblePointCount();
    return eligiblePoints >= minRequired;
  }

  startAiInsightPointSelectionFlow(_handler: MatchmakerHandler) {}

  autoSelectAiInsightPoints(_handler: MatchmakerHandler) {}

  endAiInsightPointSelectionFlow() {}

  get backgroundColor() {
    return '#ffffff';
  }

  get isFilterGroupParent() {
    return this.widgetGroup?.parentWidget === this.widget.id;
  }

  abstract get widgetQueryOptions(): WidgetQueryOptions;

  get noPermission(): boolean {
    return (
      !this.isLoading &&
      this.authService.isAlwaysRedact &&
      (this.payload.customFieldSelected || this.payload.columnCustomFieldSelected) &&
      (this.analyticItemFieldDetails?.metadata?.redacted === true ||
        this.columnAnalyticItemFieldDetails?.metadata?.redacted === true)
    );
  }

  get analyticItemFieldDetails() {
    return this.fieldDetailsMap.get(this.widget.payload.analyticItem);
  }

  get columnAnalyticItemFieldDetails() {
    return this.fieldDetailsMap.get(this.widget.payload.columnAnalyticItem);
  }

  get filteredDocumentCount() {
    return this._filteredDocumentCount;
  }

  set filteredDocumentCount(count) {
    this._filteredDocumentCount = Math.min(count, this.widgetDataSource?.documentCount ?? count);
  }

  hideOrShowAllComments() {}

  setAnnotationSelected(commentTarget: CommentTarget) {
    this.commentsHandlerService.setCommentTarget(commentTarget);
  }

  // This handler sole purpose is to handle the annotation drag event and saving the dragged position of the annotation (pinned comment)
  annotationDragHandlerFn() {
    const self = this;
    return function (event: any) {
      if (!event.target) {
        return;
      }
      const { userOptions, hasDragged } = event.target;
      const commentOnWidget = self.payload.comments!.find(
        (comment) => comment.id === userOptions.id
      );

      const newX = userOptions.labels[0].x;
      const newY = userOptions.labels[0].y;
      const prev = commentOnWidget!.payload!.dragPosition;

      if (!hasDragged) {
        return;
      }

      if (prev?.x === newX && prev?.y === newY) {
        return;
      }

      commentOnWidget!.payload!.dragPosition = { x: newX, y: newY };
    };
  }
}
